"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Zap } from "lucide-react";
import MarketplaceVerticalSnapFeed from "@/components/feed/marketplace-vertical-snap-feed";
import { type ListingForVerticalCard } from "@/components/feed/marketplace-vertical-listing-card";
import MarketplaceVerticalSnapFeedSkeleton from "@/components/feed/marketplace-vertical-snap-feed-skeleton";
import { FEED_SNAP_INDEX_KEY_FOLLOWING } from "@/lib/feed-snap-index";
import { useFollowingFeed } from "@/hooks/use-following-feed";
import { createClient } from "@/utils/supabase/client";

interface FollowingFeedProps {
  isLoggedIn: boolean;
  currentUserId?: string;
  onMobileSheetChangeAction?: (open: boolean) => void;
}

export default function FollowingFeed({ isLoggedIn, currentUserId, onMobileSheetChangeAction }: FollowingFeedProps) {
  const seenIdsRef = useRef<Set<string>>(new Set());
  const realtimeRefreshRef = useRef<number | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [newItemsCount, setNewItemsCount] = useState(0);
  const [toastContainer, setToastContainer] = useState<HTMLElement | null>(null);
  const [followedUserIds, setFollowedUserIds] = useState<string[]>([]);
  const supabase = useMemo(() => createClient(), []);
  const {
    items,
    isLoading,
    isInitialized,
    error,
    hasNextPage,
    loadMore,
    refresh,
    refetchFirstPage,
  } = useFollowingFeed(currentUserId);

  useEffect(() => {
    seenIdsRef.current = new Set();
    setToastVisible(false);
    setNewItemsCount(0);
  }, [currentUserId, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      setToastContainer(null);
      return;
    }
    const host = document.createElement("div");
    host.setAttribute("data-following-toast", "true");
    document.body.appendChild(host);
    setToastContainer(host);
    return () => {
      document.body.removeChild(host);
      setToastContainer(null);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !currentUserId) {
      setFollowedUserIds([]);
      return;
    }
    let active = true;
    supabase
      .from("follows")
      .select("followed_id")
      .eq("follower_id", currentUserId)
      .then(({ data, error }) => {
        if (!active) {
          return;
        }
        if (error) {
          console.error("following-feed follows fetch error", error);
          setFollowedUserIds([]);
          return;
        }
        setFollowedUserIds((data || []).map((row) => row.followed_id));
      });
    return () => {
      active = false;
    };
  }, [currentUserId, isLoggedIn, supabase]);

  useEffect(() => {
    if (!isLoggedIn || !currentUserId || followedUserIds.length === 0) {
      return;
    }

    const formatFilter = (values: string[]) => {
      if (values.length === 1) {
        return `user_id=eq.${values[0]}`;
      }
      const quoted = values.map((value) => `"${value}"`).join(",");
      return `user_id=in.(${quoted})`;
    };

    const triggerRefresh = () => {
      if (realtimeRefreshRef.current) {
        window.clearTimeout(realtimeRefreshRef.current);
      }
      realtimeRefreshRef.current = window.setTimeout(() => {
        refetchRef.current();
      }, 200);
    };

    const handleListingPayload = (payload: any) => {
      try {
        const record = payload?.new as {
          deleted_at?: string | null;
          quantity?: number | null;
          scheduled_publish_at?: string | null;
        } | null;
        if (!record) {
          return;
        }
        if (record.deleted_at) {
          return;
        }
        if (!record.quantity || Number(record.quantity) <= 0) {
          return;
        }
        if (record.scheduled_publish_at) {
          const scheduledTime = new Date(record.scheduled_publish_at).getTime();
          if (Number.isFinite(scheduledTime) && scheduledTime > Date.now()) {
            return;
          }
        }
      } catch {
        return;
      }
      triggerRefresh();
    };

    const handlePostPayload = () => {
      triggerRefresh();
    };

    const channel = supabase
      .channel(`following-feed-${currentUserId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "listings",
          filter: formatFilter(followedUserIds),
        },
        handleListingPayload
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "posts",
          filter: formatFilter(followedUserIds),
        },
        handlePostPayload
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      if (realtimeRefreshRef.current) {
        window.clearTimeout(realtimeRefreshRef.current);
        realtimeRefreshRef.current = null;
      }
    };
  }, [currentUserId, followedUserIds, isLoggedIn, supabase]);

  const refetchRef = useRef(refetchFirstPage);
  refetchRef.current = refetchFirstPage;


  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const interval = setInterval(() => {
      refetchRef.current();
    }, 30000);
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const handleFocus = () => refetchRef.current();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isInitialized || !items.length) {
      return;
    }
    const seen = seenIdsRef.current;
    if (!seen.size) {
      items.forEach((item) => seen.add(`${item.type}-${item.id}`));
      return;
    }
    let fresh = 0;
    for (const item of items) {
      const key = `${item.type}-${item.id}`;
      if (seen.has(key)) {
        break;
      }
      fresh += 1;
    }
    if (fresh > 0) {
      setNewItemsCount(fresh);
      setToastVisible(true);
    }
    items.forEach((item) => seen.add(`${item.type}-${item.id}`));
  }, [items, isInitialized]);

  const dismissToast = () => {
    setToastVisible(false);
  };

  const scrollToLatest = () => {
    setToastVisible(false);
    setNewItemsCount(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toastContent =
    toastVisible && toastContainer
      ? createPortal(
          <div className="fixed inset-0 z-2000 flex items-center justify-center px-4">
            <div className="pointer-events-auto relative inline-flex max-w-sm flex-col items-center gap-4 rounded-[32px] bg-black/85 px-8 py-7 text-center text-white shadow-[0_20px_110px_rgba(0,0,0,0.55)] backdrop-blur-xl">
              <button
                type="button"
                onClick={dismissToast}
                aria-label="Dismiss new following notification"
                className="absolute right-4 top-4 rounded-full bg-white/10 p-2 transition hover:bg-white/20"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="text-xs font-medium uppercase tracking-[0.35em] text-white/60">
                Following live
              </p>
              <p className="text-lg font-medium leading-snug">
                {newItemsCount === 1
                  ? "Fresh update from someone you follow"
                  : `${newItemsCount} new updates from creators you follow`}
              </p>
              <p className="text-sm text-white/70">Jump to the top to catch it</p>
              <button
                type="button"
                onClick={scrollToLatest}
                className="w-full rounded-full bg-white px-5 py-3 text-sm font-semibold uppercase tracking-wide text-black transition hover:bg-white/90"
              >
                View latest
              </button>
            </div>
          </div>,
          toastContainer
        )
      : null;

  if (!isLoggedIn) {
    return (
      <div className="text-center py-16 text-gray-500">
        Please log in to see content from users you follow
      </div>
    );
  }

  const isInitialLoading = !isInitialized && isLoading;
  const isLoadingMore = isInitialized && isLoading;
  const listingItems = useMemo(
    () => items.filter((item) => item.type === "listing"),
    [items]
  );
  const listings = useMemo(
    () =>
      listingItems.map((item: any) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        price: item.price || "0",
        currency: item.currency ?? null,
        image_urls: item.image_urls,
        shipFromAddress: item.shipFromAddress ?? null,
        condition: item.condition || "",
        grade: item.grade || "",
        created_at: item.created_at,
        user_id: item.user_id,
        quantity: item.quantity || 0,
        sellerUsername: item.sellerUsername,
        sellerAvatarUrl: item.sellerAvatarUrl,
        sellerIsFollowing: item.sellerIsFollowing,
        initialSaved: item.initialSaved || false,
        scheduled_publish_at: item.scheduled_publish_at ?? null,
        listing_format: item.listing_format ?? null,
        auction_start_price: item.auction_start_price ?? null,
        auction_highest_bid: item.auction_highest_bid ?? null,
        auction_end_at: item.auction_end_at ?? null,
        auction_status: item.auction_status ?? null,
        auction_bid_count: item.auction_bid_count ?? 0,
        type: (item as { type?: "standard" | "preorder" | null }).type ?? null,
        sellerSalesCount: item.sellerSalesCount,
        card_details: item.card_details ?? null,
        // Phase 3 (2026-06-29): `accepts_offers` collapsed — derive via
        // `isOfferEnabledFormat(listing_format)`.
        offers_pending_count: (item as { offers_pending_count?: number | null }).offers_pending_count ?? 0,
        // Pass the API-computed saved count straight through so the card seeds
        // it (no per-card Supabase count query). Undefined stays undefined →
        // card shows no count (RPC-soft-fail degrade contract).
        saves_count: (item as { saves_count?: number }).saves_count,
      } satisfies ListingForVerticalCard)),
    [listingItems]
  );

  return (
    <>
      {toastContent}
      {error ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center space-y-8 max-w-md px-4">
            <div className="inline-block">
              <Zap
                className="w-20 h-20 text-foreground opacity-90 animate-bounce"
                strokeWidth={1.5}
              />
            </div>

            <div className="space-y-3">
              <h1 className="text-4xl tracking-tight text-foreground">
                Oops!
              </h1>
              <p className="text-lg text-muted-foreground tracking-wide">
                We broke our toy
              </p>
            </div>

            <div className="pt-6">
              <button
                onClick={refresh}
                className="px-8 py-3 border border-foreground text-foreground tracking-wide hover:bg-foreground hover:text-background transition-all duration-300 ease-in-out rounded-full"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : (
      <div className="space-y-4 h-full min-h-0 flex flex-col">

      {isInitialLoading ? <MarketplaceVerticalSnapFeedSkeleton /> : null}

      {!error && listings.length > 0 ? (
        <div className="flex-1 min-h-0 flex flex-col">
        <MarketplaceVerticalSnapFeed
          listings={listings}
          currentUserId={currentUserId}
          hasNextPage={hasNextPage}
          isLoadingMore={isLoadingMore}
          onLoadMoreAction={loadMore}
          onFollowChangeAction={refetchFirstPage}
          onToggleSavedAction={refetchFirstPage}
          onMobileSheetChangeAction={onMobileSheetChangeAction}
          snapIndexStorageKey={FEED_SNAP_INDEX_KEY_FOLLOWING}
        />
        </div>
      ) : null}

      {isInitialized && listings.length === 0 && !error && (
        <div className="text-center py-16 text-gray-500">
          No listings from users you follow yet
        </div>
      )}

      {isInitialized && !hasNextPage && listings.length > 0 && (
        <div className="hidden md:block text-center py-8 text-gray-500">
          No more content to load
        </div>
      )}
    </div>
      )}
    </>
  );
}

